import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PaymentMethod } from "@/types/types";
import { Minus, Plus } from "lucide-react";
import { useMemo } from "react";

export type PaymentSplitDraft = { mode: PaymentMethod; amount: string };

function parseAmount(s: string): number | null {
  const n = Number(String(s ?? "").trim());
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return null;
  return n;
}

export function getSplitsTotal(splits: PaymentSplitDraft[]): number {
  return splits.reduce((sum, sp) => sum + (parseAmount(sp.amount) ?? 0), 0);
}

export function getSplitsError(opts: { splits: PaymentSplitDraft[]; maxTotal: number }): string | null {
  const { splits, maxTotal } = opts;
  // Nothing to collect (e.g. 100% discount / fully waived bill)
  if (maxTotal <= 0) return null;
  if (!splits.length) return "Add at least one payment mode";
  for (const sp of splits) {
    if (!sp.mode) return "Select payment mode";
    if (parseAmount(sp.amount) === null) return "Enter a valid amount (> 0) for each mode";
  }
  const total = getSplitsTotal(splits);
  if (total <= 0) return "Total must be > 0";
  if (total > maxTotal) return `Total cannot exceed due amount (₹${maxTotal.toFixed(2)})`;
  return null;
}

export default function PaymentSplitsEditor(props: {
  splits: PaymentSplitDraft[];
  onChange: (next: PaymentSplitDraft[]) => void;
  maxTotal: number;
  disabled?: boolean;
  label?: string;
}) {
  const { splits, onChange, maxTotal, disabled, label } = props;

  const total = useMemo(() => getSplitsTotal(splits), [splits]);
  const error = useMemo(() => getSplitsError({ splits, maxTotal }), [splits, maxTotal]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label>{label ?? "Payment modes"}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...splits, { mode: PaymentMethod.CASH, amount: "" }])}
          className="h-8"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add mode
        </Button>
      </div>

      <div className="space-y-2">
        {splits.map((sp, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-5 space-y-1">
              <Label className="text-xs">Mode</Label>
              <Select
                value={sp.mode}
                onValueChange={(v) => onChange(splits.map((x, i) => (i === idx ? { ...x, mode: v as PaymentMethod } : x)))}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(PaymentMethod).map((m) => (
                    <SelectItem key={m} value={m}>
                      {String(m).replace(/_/g, " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-5 space-y-1">
              <Label className="text-xs">Amount (₹)</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={sp.amount}
                onChange={(e) => onChange(splits.map((x, i) => (i === idx ? { ...x, amount: e.target.value } : x)))}
                disabled={disabled}
                placeholder="0"
              />
            </div>

            <div className="col-span-2 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={disabled || splits.length <= 1}
                onClick={() => onChange(splits.filter((_, i) => i !== idx))}
              >
                <Minus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="text-muted-foreground">Total</div>
        <div className="font-medium">₹{total.toFixed(2)}</div>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
    </div>
  );
}

